import React, { forwardRef, ReactElement, useEffect, useImperativeHandle, useState } from 'react';
import {
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  TableSortLabel,
} from '@mui/material';
import { UILoader } from '../ui-loader';
import _ from 'lodash';

import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { SortDirection, UITableColumn, UITableProps, UITableRef } from './types';
import { FieldPath } from 'react-hook-form';
import { UIPagination } from '../ui-pagination';

const renderSubHeaderRow = <T extends object>(columns: UITableColumn<T>[]) => {
  return (
    <TableRow>
      {columns.flatMap((column) =>
        column.columns
          ? column.columns.map((subColumn, subIndex) => (
              <TableCell
                key={`${column.label}-${subIndex}`}
                align={subColumn.headerAlign}
                sx={{
                  ...subColumn?.sx,
                  ...(subColumn?.nowrap
                    ? {
                        whiteSpace: 'nowrap',
                        width: '2%',
                      }
                    : {}),
                }}
              >
                {subColumn.label}
              </TableCell>
            ))
          : []
      )}
    </TableRow>
  );
};

const renderTotalRow = <T extends object>(
  columns: UITableColumn<T>[],
  totals: Partial<Record<FieldPath<T>, unknown>>
) => (
  <TableRow
    sx={{
      backgroundColor: '#1555FC0A',
    }}
  >
    {columns.map((column, columnIndex) => (
      <TableCell
        key={columnIndex}
        align={column.headerAlign}
        sx={{
          fontWeight: 600,
        }}
      >
        {!columnIndex
          ? i18next.t('total')
          : column.totalKey
          ? String(totals[column.totalKey]) || null
          : null}
      </TableCell>
    ))}
  </TableRow>
);

const Comp = <T extends object>(props: UITableProps<T>, ref: React.ForwardedRef<UITableRef>) => {
  const { t } = useTranslation();
  const {
    columns,
    data,
    PaginationProps,
    sx,
    checkbox,
    EmptyPlaceholder,
    isLoading,
    addittionRows,
    onRowClick,
    hoveredElementOnClick,
    hoveredBtnText = t('confirm'),
    totals,
    sort: { sortKey, sortDirection, onSortChange } = {},
    getRowDisabled,
  } = props;

  const [selected, setSelected] = useState<T[]>([]);
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

  useEffect(() => {
    if (typeof checkbox === 'object') {
      setSelected(checkbox.selected);
    }
  }, [checkbox]);

  useImperativeHandle(
    ref,
    () => ({
      sortKey,
      sortDirection,
    }),
    [sortKey, sortDirection]
  );

  const handleSortChange = (column: UITableColumn<T>) => {
    if (column?.sortKey) {
      const isAsc = sortKey === column.sortKey && sortDirection === 'asc';
      const newDirection: SortDirection = isAsc ? 'desc' : 'asc';
      onSortChange?.(column?.sortKey || '', newDirection);
    }
  };

  if (isLoading) {
    return <UILoader />;
  }

  if (!data || data.length === 0) {
    return EmptyPlaceholder || <Typography py={2}>Нет данных</Typography>;
  }

  const renderCellContent = (column: UITableColumn<T>, row: T, index: number) => {
    if (column.render) {
      return column.render(row, index);
    }
    if (column.key) {
      const value = _.get(row, column.key as string);
      if (React.isValidElement(value)) {
        return value;
      }
      return String(value);
    }
    return null;
  };

  const flattenColumns = (columns: UITableColumn<T>[]): UITableColumn<T>[] => {
    return columns.flatMap((column) => (column.columns ? column.columns : column));
  };

  const flatColumns = flattenColumns(columns);

  return (
    <TableContainer sx={sx}>
      <Table>
        <TableHead>
          <TableRow>
            {checkbox && (
              <TableCell padding='checkbox' rowSpan={2}>
                <Checkbox
                  checked={data
                    .filter((item, i) => !getRowDisabled?.(item, i))
                    .every((item) =>
                      selected.some((selectedItem) => _.isEqual(selectedItem, item))
                    )}
                  onChange={(e) => {
                    if (typeof checkbox !== 'object') return;
                    if (e.target.checked) {
                      const enabledData = data.filter((item, i) => !getRowDisabled?.(item, i));
                      checkbox.setSelected((prev) =>
                        _.uniqWith([...prev, ...enabledData], _.isEqual)
                      );
                    } else {
                      checkbox.setSelected((prev) => {
                        return prev.filter((item) => !data.includes(item));
                      });
                    }
                  }}
                />
              </TableCell>
            )}
            {columns.map((column, index) => {
              if (column.columns) {
                return (
                  <TableCell
                    key={index}
                    colSpan={column.columns.length}
                    align={column.headerAlign}
                    sx={{
                      textTransform: 'none',
                      ...column?.sx,
                      ...(column.nowrap ? { whiteSpace: 'nowrap', width: '2%' } : {}),
                    }}
                  >
                    {column.label}
                  </TableCell>
                );
              }
              return (
                <TableCell
                  key={index}
                  rowSpan={2}
                  align={column.headerAlign}
                  sx={{
                    textTransform: 'none',
                    ...column?.sx,
                    ...(column.nowrap ? { whiteSpace: 'nowrap', width: '2%' } : {}),
                  }}
                >
                  {column.sortKey ? (
                    <TableSortLabel
                      active={sortKey === column.sortKey}
                      direction={sortKey === column.sortKey ? (sortDirection as 'desc') : 'asc'}
                      onClick={() => handleSortChange(column)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              );
            })}
          </TableRow>
          {renderSubHeaderRow(columns)}
        </TableHead>
        <TableBody>
          {totals && renderTotalRow(columns, totals)}
          {data.map((row, index) => (
            <React.Fragment key={index}>
              <TableRow
                hover
                onClick={() => onRowClick && onRowClick(row, index)}
                role='checkbox'
                aria-checked={selected.some((item) => _.isEqual(item, row))}
                tabIndex={-1}
                selected={selected.some((item) => _.isEqual(item, row))}
                onMouseEnter={() => setHoveredRowIndex(index)}
                onMouseLeave={() => setHoveredRowIndex(null)}
                sx={{
                  position: 'relative',
                  pointerEvents: getRowDisabled?.(row, index) ? 'none' : 'auto',
                }}
              >
                {checkbox && (
                  <TableCell padding='checkbox'>
                    <Checkbox
                      color='primary'
                      checked={selected.some((item) => _.isEqual(item, row))}
                      disabled={getRowDisabled?.(row, index) ?? false}
                      onChange={() => {
                        const newSelected = [...selected];
                        const selectedIndex = selected?.findIndex((item) => _.isEqual(item, row));
                        if (selectedIndex === -1) {
                          newSelected.push(row);
                        } else {
                          newSelected.splice(selectedIndex, 1);
                        }
                        setSelected(newSelected);
                        if (typeof checkbox === 'object') {
                          checkbox.setSelected(newSelected);
                        }
                      }}
                    />
                  </TableCell>
                )}
                {flatColumns.map((column, columnIndex) => (
                  <TableCell
                    key={columnIndex}
                    align={column.align}
                    sx={{
                      ...column?.sx,
                      ...(column?.nowrap
                        ? {
                            whiteSpace: 'nowrap',
                            width: '2%',
                          }
                        : {}),
                    }}
                  >
                    {renderCellContent(column, row, index)}
                  </TableCell>
                ))}
                {hoveredElementOnClick && hoveredRowIndex === index && (
                  <TableCell
                    sx={{
                      position: 'absolute',
                      right: 0,
                      border: 'none',
                    }}
                  >
                    <Button
                      variant='contained'
                      size='large'
                      onClick={() => hoveredElementOnClick(row, index)}
                    >
                      {hoveredBtnText}
                    </Button>
                  </TableCell>
                )}
              </TableRow>
              {addittionRows && addittionRows(row, index)}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
      {PaginationProps && <UIPagination {...PaginationProps} />}
    </TableContainer>
  );
};

export const UITable = forwardRef(Comp) as <T extends object>(
  props: UITableProps<T> & {
    ref?: React.ForwardedRef<UITableRef>;
  }
) => ReactElement<UITableProps<T>>;
